import React from 'react';
import { connect } from 'react-redux';
import { responseErrors } from '@/utils/errors';
import { saveStates } from '@/constants/lifecycle';
import { updataDatasetValidationErrors, updateActiveDatasetStep } from '../actions';
import DatasetSteps from './DatasetSteps';
import { datasetSteps } from '../constants';
import { resolveCatalogueTemplate } from '../catalogue/helper';
import DatasetTypeActions from './DatasetTypeActions';
import FirstCatalogueSectionActions, {
  validateFirstSectionStep,
} from '../catalogue/components/FirstCatalogueSectionActions';
import CatalogueSectionActions, {
  validateSecondSectionStep,
  validateThirdSectionStep,
} from '../catalogue/components/CatalogueSectionActions';
import DatasetSettingsActions from '../settings/containers/DatasetSettingsActions';
import DatasetWizardContent from '../components/DatasetWizardContent';
import DictionariesActions, { validateDictionariesStep } from '../dictionaries/containers/DictionariesActions';
import { datasetSourceAndStepAttributes, previousDatasetStepIndex, nextDatasetStepIndex } from '../state/state';
import { datasetPerms } from '../sharing/state/state';
import AttachmentsActions from '../attachments/components/AttachmentsActions';

const wizardActions = {
  [datasetSteps.START.name]: { component: DatasetTypeActions },
  [datasetSteps.FIRST_SECTION.name]: { component: FirstCatalogueSectionActions, validator: validateFirstSectionStep },
  [datasetSteps.SECOND_SECTION.name]: { component: CatalogueSectionActions, validator: validateSecondSectionStep },
  [datasetSteps.THIRD_SECTION.name]: { component: CatalogueSectionActions, validator: validateThirdSectionStep },
  [datasetSteps.ATTACHMENTS.name]: { component: AttachmentsActions },
  [datasetSteps.DICTIONARIES.name]: { component: DictionariesActions, validateDictionariesStep },
  [datasetSteps.SETTINGS.name]: { component: DatasetSettingsActions },
};

const DatasetWizard = ({
  activeStepIndex,
  allowAttachments,
  category,
  code,
  compact,
  dataset,
  readErrors,
  saveErrors,
  template,
  title,
  updateErrors,
  updateStep,
  url,
}) => {
  const activeStep = Object.values(datasetSteps)[activeStepIndex].name;

  const validateStep = () => {
    const validator = wizardActions[activeStep]?.validator;
    return validator ? validator({ dataset, template }) : [];
  };

  const goToStep = (index) => {
    if (index < Object.values(datasetSteps).length && index >= 0) {
      updateStep(category, code, index, template);
    }
  };

  const goForward = () => {
    const validationErrors = validateStep();
    updateErrors(category, code, [...validationErrors, ...responseErrors(saveErrors)]);

    if (validationErrors.length === 0) {
      goToStep(nextDatasetStepIndex(dataset, { template, allowAttachments }));
    }
  };

  const DatasetWizardActions = wizardActions[activeStep]?.component;

  return (
    <div className={compact ? 'tab-wizard-compact' : 'tab-wizard'}>
      <DatasetSteps category={category} code={code} compact={compact} goToStep={goToStep} url={url} />
      <DatasetWizardContent
        activeStep={activeStep}
        category={category}
        code={code}
        compact={compact}
        readErrors={readErrors}
        title={title}
        url={url}
      />
      <DatasetWizardActions
        allowAttachments={allowAttachments}
        category={category}
        code={code}
        compact={compact}
        goBack={() => goToStep(previousDatasetStepIndex(dataset, { template, allowAttachments }))}
        goForward={goForward}
      />
    </div>
  );
};

const mapStateToProps = ({
  auth: { role: { permissions: { attachments: { create } } } },
  catalogues: { templates },
  datasets: { datasets },
  datasetDatasource: { features },
}, ownProps) => {
  const { dataset, template } = resolveCatalogueTemplate(datasets, templates, ownProps, {
    catalogue: { title: '' },
    permissions: datasetPerms(),
    ...saveStates,
    steps: datasetSourceAndStepAttributes(),
  }, features);

  return ({
    activeStepIndex: dataset.steps.activeStepIndex,
    allowAttachments: create,
    dataset,
    title: dataset.catalogue.title,
    readErrors: dataset.readErrors.concat(template.readErrors),
    saveErrors: dataset.saveErrors,
    template,
  });
};

const mapDispatchToProps = {
  updateStep: updateActiveDatasetStep,
  updateErrors: updataDatasetValidationErrors,
};

export default connect(mapStateToProps, mapDispatchToProps)(DatasetWizard);
